import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Tooltip, IconButton, Fab } from '@material-ui/core';
import { Add, Refresh, Delete } from '@material-ui/icons';
import { BackButton, hasAtLeastOnePermission, getDimensionsOfPagination } from '../../sc-utilities';
import MaterialTable from 'material-table';
import { hasPermission, localization } from '../../sc-utilities';
import * as actions from './store/customerActions';
import * as action from '../../np-core/Auth/store/authActions';
import { MuiTableRows } from '../../../themes';
import { CancelToken } from 'axios';
import DangerDelete from '../../sc-components/DangerDelete';
import Toolbar from '../../sc-utilities/Toolbar';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const CustomersList = props => {
    const { classes, translations, permissions, onFetchCustomers, onFetchCustomersCount, fetching, saveColumnsFilter, activeCustomerID, tableColumns, redirectHandler, deleting, onBulkDeleteCustomers, tableRef } = props;

    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);
    const [deleteModalState, setDeleteModalState] = useState({ open: false, customerIDs: [] });

    const countSource = CancelToken.source();
    const listSource = CancelToken.source();

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        filterCellStyle: { maxWidth: '200px' },
        selection: hasAtLeastOnePermission(permissions, ['customer_delete']),
        search: false,
        minBodyHeight: window.innerHeight - 197 < 300 ? 300 : window.innerHeight - 197,
        maxBodyHeight: window.innerHeight - 197,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    useEffect(() => {
        return () => {
            countSource.cancel('Operation canceled by the user.');
            listSource.cancel('Operation canceled by the user.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };
    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        let count = await onFetchCustomersCount(query, { countSource: countSource });
        if (!query['page'] && !rowsPageSizeInit.includes(query['pageSize'])) {
            let dimensions = getDimensionsOfPagination(count.data)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }

        let result = count.data ? await onFetchCustomers(query, { listSource: listSource }) : { data: [] };
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result.data, page: query.page, pageSize: pageSize, totalCount: count.data };
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations['customers'] || 'Customers'}</Typography>
                {hasPermission(permissions, 'customer_create') ? <Fab
                    id="create_customer"
                    variant="extended"
                    size="small"
                    color="primary"
                    aria-label="Add"
                    style={{ borderRadius: '1px' }}
                    className={classes.fab}
                    onClick={() => redirectHandler(`/admin/customers/create`)}
                >
                    <Add size="small" />
                    {translations["create_new"] || "Create new"}
                </Fab> : null}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    };

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, customerIDs: [] });
    };

    const handleCloseDanger = async () => {
        await onBulkDeleteCustomers({ CustomerIDs: deleteModalState.customerIDs, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, customerIDs: [] });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const handleBulkDelete = customerIDs => {
        setDeleteModalState({ open: true, customerIDs: customerIDs });
    };

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["customers"] || "Customers"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(customer => customer.CustomerID))
        }
    ];

    const columnHidden = newColumnState => {
        let objToSendColumn = {};
        let objColumns = {};
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden;
            return objColumns;
        })
        objToSendColumn[`Customer`] = objColumns;
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID });
        return newColumnState;
    }

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar
                            data={props}
                            actions={tableActions}
                            selectedRows={props.selectedRows}
                            translations={translations}
                        />
                    )
                }}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    setTableOptions({ ...tableOptions, pageSize: size })
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {
                hasPermission(permissions, 'customer_delete') ? (
                    <DangerDelete
                        entityName={translations['customers'] || 'Customers'}
                        translations={translations}
                        handleCloseDanger={handleCloseDanger}
                        handleCloseSafe={handleCloseSafe}
                        open={deleteModalState?.open}
                        deleting={deleting}
                    />
                ) : null
            }
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID,
        translations: state?.translate?.translations?.activeTranslations || {},
        permissions: state?.core?.login?.permissions || [],
        fetching: state?.customer?.customers?.fetching,
        deleting: state?.customer?.customers?.deleting
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchCustomersCount: (filter, options) => dispatch(actions.fetchCustomersCount(filter, options)),
        onFetchCustomers: (filter, options) => dispatch(actions.fetchCustomers(filter, options)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        onBulkDeleteCustomers: params => dispatch(actions.bulkDeleteCustomers(params)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(CustomersList));